/**
 * SegmentPicker — выбор сегмента для ручной рассылки
 * Дата: 26.02.2026 — PLAN Manual Broadcasts
 */
import { useState, useEffect } from 'react';
import { manualBroadcastsApi, type SegmentInfo, type DynamicSegmentInfo, type LkSegmentInfo } from '@/api/manualBroadcasts';
import { Select } from '@/components/ui/Select';

type SegmentType = 'regular' | 'dynamic' | 'lk';
type AnySegment = SegmentInfo | DynamicSegmentInfo | LkSegmentInfo;

interface SegmentPickerProps {
  segmentType: SegmentType;
  segmentId: number;
  segmentName: string;
  tenantId?: string;
  onSelect: (id: number, name: string, type: SegmentType) => void;
  disabled?: boolean;
}

function sortSegmentsNewestFirst<T extends AnySegment>(segments: T[]): T[] {
  return [...segments].sort((a, b) => {
    const aTs = Date.parse(a.created_at || '');
    const bTs = Date.parse(b.created_at || '');
    const aHasDate = Number.isFinite(aTs);
    const bHasDate = Number.isFinite(bTs);
    if (aHasDate && bHasDate) return bTs - aTs;
    if (aHasDate) return -1;
    if (bHasDate) return 1;
    return (b.id || 0) - (a.id || 0);
  });
}

export function SegmentPicker({ segmentType, segmentId, segmentName: _segmentName, tenantId, onSelect, disabled }: SegmentPickerProps) {
  const [regularSegments, setRegularSegments] = useState<SegmentInfo[]>([]);
  const [dynamicSegments, setDynamicSegments] = useState<DynamicSegmentInfo[]>([]);
  const [lkSegments, setLkSegments] = useState<LkSegmentInfo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        if (segmentType === 'dynamic') {
          const dynamic = await manualBroadcastsApi.listDynamicSegments(tenantId);
          if (!cancelled) {
            setDynamicSegments(sortSegmentsNewestFirst(dynamic));
          }
        } else if (segmentType === 'lk') {
          const lk = await manualBroadcastsApi.listLkSegments(tenantId);
          if (!cancelled) {
            setLkSegments(sortSegmentsNewestFirst(lk));
          }
        } else {
          const regular = await manualBroadcastsApi.listSegments(tenantId);
          if (!cancelled) {
            setRegularSegments(sortSegmentsNewestFirst(regular));
          }
        }
      } catch (e) {
        if (!cancelled) {
          if (segmentType === 'dynamic') setDynamicSegments([]);
          else if (segmentType === 'lk') setLkSegments([]);
          else setRegularSegments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, segmentType]);

  const segments =
    segmentType === 'dynamic'
      ? dynamicSegments
      : segmentType === 'lk'
        ? lkSegments
        : regularSegments;
  const options = [
    { value: '', label: 'Выберите сегмент' },
    ...segments.map((s) => ({
      value: String(s.id),
      label: `${s.name}${typeof s.patients_count === 'number' ? ` (${s.patients_count})` : ('count' in s && typeof s.count === 'number' ? ` (${s.count})` : '')}`,
    })),
  ];

  return (
    <Select
      value={segmentId ? String(segmentId) : ''}
      onChange={(e) => {
        const v = e.target.value;
        if (!v) {
          onSelect(0, '', segmentType);
          return;
        }
        const id = parseInt(v, 10);
        const seg = segments.find((s) => s.id === id);
        onSelect(id, seg?.name ?? '', segmentType);
      }}
      options={options}
      disabled={disabled || loading}
    />
  );
}
